<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Popover from 'primevue/popover'
import { useI18n } from '@/i18n'
import { useFormatDate } from '@/composables/useFormatDate'
import { useNotificationsStore } from '@/stores/notifications'

const { t }      = useI18n()
const { fmtDate } = useFormatDate()
const router     = useRouter()
const store      = useNotificationsStore()

const op = ref(null)

const unread = computed(() => store.unreadCount)
const badgeText = computed(() => (unread.value > 99 ? '99+' : String(unread.value)))

const SEV_SEVERITY = {
  critical: 'danger', high: 'danger', medium: 'warn', low: 'info', info: 'secondary',
}

function toggle(e) {
  store.fetchInbox()          // beim Öffnen frisch laden
  op.value?.toggle(e)
}

async function openItem(item) {
  if (!item.read_at) await store.markRead(item.id)
  op.value?.hide()
  if (item.link) router.push(item.link).catch(() => {})
}

function onMarkAll() { store.markAllRead() }

function goSettings() {
  op.value?.hide()
  router.push('/user/settings/notifications').catch(() => {})
}

onMounted(() => { store.fetchUnread() })
</script>

<template>
  <div class="notif-bell">
    <button
      class="rail-btn notif-bell-btn"
      v-tooltip.right="t('notifications.bellTitle')"
      @click="toggle"
    >
      <i class="pi pi-bell" />
      <span v-if="unread > 0" class="notif-badge">{{ badgeText }}</span>
    </button>

    <Popover ref="op" :pt="{ root: { class: 'notif-pop' } }">
      <div class="notif-panel">
        <div class="notif-panel-head">
          <span class="notif-panel-title">{{ t('notifications.bellTitle') }}</span>
          <button
            v-if="unread > 0"
            class="notif-link-btn"
            @click="onMarkAll"
          >{{ t('notifications.markAllRead') }}</button>
        </div>

        <div v-if="store.loading && !store.items.length" class="notif-empty">
          <i class="pi pi-spin pi-spinner" />
        </div>

        <ul v-else-if="store.items.length" class="notif-list">
          <li
            v-for="item in store.items"
            :key="item.id"
            class="notif-item"
            :class="{ 'notif-item--unread': !item.read_at }"
            @click="openItem(item)"
          >
            <span class="notif-item-dot" :class="`sev-${item.severity}`" />
            <div class="notif-item-body">
              <div class="notif-item-toprow">
                <span class="notif-item-title">{{ item.title }}</span>
                <Tag
                  :value="t('notifications.sev_' + item.severity)"
                  :severity="SEV_SEVERITY[item.severity] || 'secondary'"
                  style="font-size:.62rem"
                />
              </div>
              <p v-if="item.body" class="notif-item-text">{{ item.body }}</p>
              <span class="notif-item-time">{{ fmtDate(item.created_at) }}</span>
            </div>
          </li>
        </ul>

        <div v-else class="notif-empty">
          <i class="pi pi-inbox" />
          <span>{{ t('notifications.bellEmpty') }}</span>
        </div>

        <div class="notif-panel-foot">
          <button class="notif-link-btn" @click="goSettings">
            <i class="pi pi-cog" /> {{ t('notifications.bellViewAll') }}
          </button>
        </div>
      </div>
    </Popover>
  </div>
</template>

<style scoped>
.notif-bell { position: relative; }
.notif-bell-btn { position: relative; }

.notif-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--p-red-500, #ef4444);
  color: #fff;
  font-size: .62rem;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.notif-panel { width: 340px; max-width: 88vw; }
.notif-panel-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 4px 4px 10px; border-bottom: 1px solid var(--p-content-border-color, rgba(0,0,0,.08));
}
.notif-panel-title { font-size: .9rem; font-weight: 700; color: var(--p-text-color); }

.notif-link-btn {
  background: none; border: none; cursor: pointer;
  color: var(--p-primary-color, #3b82f6);
  font-size: .76rem; font-weight: 600;
  display: inline-flex; align-items: center; gap: 5px; padding: 4px 6px;
}
.notif-link-btn:hover { text-decoration: underline; }

.notif-list { list-style: none; margin: 0; padding: 4px 0; max-height: 360px; overflow-y: auto; }
.notif-item {
  display: flex; gap: 10px; padding: 10px 6px; cursor: pointer; border-radius: 8px;
}
.notif-item:hover { background: var(--p-surface-100, rgba(0,0,0,.03)); }
.notif-item--unread { background: var(--p-primary-50, #eff6ff); }

.notif-item-dot { width: 8px; height: 8px; border-radius: 999px; margin-top: 6px; flex-shrink: 0; }
.sev-critical { background: var(--p-red-600, #dc2626); }
.sev-high     { background: var(--p-red-500, #ef4444); }
.sev-medium   { background: var(--p-amber-500, #f59e0b); }
.sev-low      { background: var(--p-blue-500, #3b82f6); }
.sev-info     { background: var(--p-surface-400, #94a3b8); }

.notif-item-body { min-width: 0; flex: 1; }
.notif-item-toprow { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.notif-item-title {
  font-size: .82rem; font-weight: 600; color: var(--p-text-color);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.notif-item-text {
  font-size: .76rem; color: var(--p-text-color-secondary);
  margin: 2px 0 0; line-height: 1.35;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.notif-item-time { font-size: .68rem; color: var(--p-text-color-secondary); opacity: .8; }

.notif-empty {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 28px 12px; color: var(--p-text-color-secondary); font-size: .8rem;
}
.notif-empty i { font-size: 1.3rem; opacity: .6; }

.notif-panel-foot {
  border-top: 1px solid var(--p-content-border-color, rgba(0,0,0,.08));
  padding: 8px 4px 2px; display: flex; justify-content: center;
}

:global(.my-app-dark) .notif-item:hover { background: var(--p-surface-700, #334155); }
:global(.my-app-dark) .notif-item--unread { background: var(--p-primary-900, #1e3a5f); }
</style>
